/**
 * Settings Zod Schemas
 *
 * Validation schemas for all 7 settings categories.
 * Used with React Hook Form via zodResolver for form validation.
 *
 * Each schema matches its corresponding SettingsData interface
 * and provides field-level validation with user-friendly error messages.
 */

import { z } from "zod";
import {
  PROVINCE_CODES,
  MARITAL_STATUS_OPTIONS,
  WITHDRAWAL_ORDER_OPTIONS,
} from "./settings";

/** Non-negative number, defaults to 0 when unset */
const nonNegativeField = z.number().min(0, "Must be non-negative").default(0);

/** Non-negative rate stored as decimal (e.g. 0.05 for 5%), defaults to 0 */
const rateField = z.number().min(0, "Must be non-negative").default(0);

// ─── Profile ─────────────────────────────────────────────────

export const profileSchema = z.object({
  yearOfBirth: z
    .number()
    .min(1924, "Year must be 1924 or later")
    .max(2010, "Year must be 2010 or earlier")
    .nullable()
    .refine((val) => val !== null, { message: "Year of birth is required" }),
  isRetired: z
    .boolean()
    .nullable()
    .refine((val) => val !== null, {
      message: "Please select your retirement status",
    }),
  province: z.enum(PROVINCE_CODES).optional(),
  maritalStatus: z.enum(MARITAL_STATUS_OPTIONS).optional(),
  spouseYearOfBirth: z.number().min(1924).max(2010).nullish(),
  retirementAge: z
    .number()
    .min(50, "Retirement age must be between 50 and 80")
    .max(80, "Retirement age must be between 50 and 80")
    .optional(),
  lifeExpectancy: z
    .number()
    .min(60, "Life expectancy must be between 60 and 110")
    .max(110, "Life expectancy must be between 60 and 110")
    .optional(),
  cppStartAge: z
    .number()
    .min(60, "CPP start age must be between 60 and 70")
    .max(70, "CPP start age must be between 60 and 70")
    .optional(),
  oasStartAge: z
    .number()
    .min(65, "OAS start age must be between 65 and 70")
    .max(70, "OAS start age must be between 65 and 70")
    .optional(),
  yearsInCanadaAfterAge18: z
    .number()
    .min(0, "Years in Canada must be between 0 and 52")
    .max(52, "Years in Canada must be between 0 and 52")
    .optional(),
});

// ─── Investable Assets ───────────────────────────────────────

export const investableAssetsSchema = z.object({
  cashSavings: nonNegativeField,
  emergencyFund: nonNegativeField,
  gicTermDeposits: nonNegativeField,
  rrspBalance: nonNegativeField,
  spousalRrspBalance: nonNegativeField,
  tfsaBalance: nonNegativeField,
  fhsaBalance: nonNegativeField,
  respBalance: nonNegativeField,
  liraBalance: nonNegativeField,
  rrifBalance: nonNegativeField,
  dpspBalance: nonNegativeField,
  nonRegisteredInvestments: nonNegativeField,
  dbPensionCommutedValue: nonNegativeField,
  dcPensionBalance: nonNegativeField,
  groupRrspBalance: nonNegativeField,
  stockOptionsRsusValue: nonNegativeField,
});

// ─── Real Estate Assets ──────────────────────────────────────

export const realEstateAssetsSchema = z.object({
  primaryResidence: nonNegativeField,
  vacationProperty: nonNegativeField,
  investmentProperties: nonNegativeField,
  landOtherRealEstate: nonNegativeField,
  businessValue: nonNegativeField,
  shareholderLoansReceivable: nonNegativeField,
  insuranceCashValue: nonNegativeField,
  otherAssets: nonNegativeField,
  depreciatingAssets: nonNegativeField,
});

// ─── Liabilities ─────────────────────────────────────────────

export const liabilitiesSchema = z.object({
  // Balances
  mortgage: nonNegativeField,
  creditCardBalance: nonNegativeField,
  carLoan: nonNegativeField,
  lineOfCredit: nonNegativeField,
  studentLoans: nonNegativeField,
  otherLiabilities: nonNegativeField,
  // Rates
  mortgageInterestRate: rateField,
  creditCardInterestRate: rateField,
  carLoanInterestRate: rateField,
  lineOfCreditInterestRate: rateField,
  studentLoanInterestRate: rateField,
  otherLiabilitiesInterestRate: rateField,
  // Expanded
  investmentPropertyMortgage: nonNegativeField,
  investmentPropertyMortgageRate: rateField,
  heloc: nonNegativeField,
  helocInterestRate: rateField,
  businessLoans: nonNegativeField,
  businessLoansInterestRate: rateField,
  taxesPayable: nonNegativeField,
  mortgageAmortizationRemaining: nonNegativeField,
});

// ─── Income ──────────────────────────────────────────────────

export const incomeSchema = z.object({
  employmentIncome: nonNegativeField,
  pensionIncome: nonNegativeField,
  cppIncome: nonNegativeField,
  oasIncome: nonNegativeField,
  investmentIncome: nonNegativeField,
  rentalIncome: nonNegativeField,
  otherIncome: nonNegativeField,
  bonusVariableIncome: nonNegativeField,
  salaryGrowthRate: rateField,
  businessIncome: nonNegativeField,
});

// ─── Expenses ────────────────────────────────────────────────

export const expensesSchema = z.object({
  housing: nonNegativeField,
  utilities: nonNegativeField,
  food: nonNegativeField,
  transportation: nonNegativeField,
  insurance: nonNegativeField,
  healthcare: nonNegativeField,
  other: nonNegativeField,
  personalDiscretionary: nonNegativeField,
  mortgagePayment: nonNegativeField,
  creditCardPayment: nonNegativeField,
  carLoanPayment: nonNegativeField,
  lineOfCreditPayment: nonNegativeField,
  studentLoanPayment: nonNegativeField,
  otherLiabilitiesPayment: nonNegativeField,
  // PlannedExpense[] is validated separately (complex nested type)
  plannedExpenses: z
    .array(
      z.object({
        description: z.string(),
        amount: z.number().min(0),
        year: z.number().min(2000),
      })
    )
    .optional(),
});

// ─── Assumptions ─────────────────────────────────────────────

export const assumptionsSchema = z.object({
  retirementAge: nonNegativeField,
  lifeExpectancy: nonNegativeField,
  expectedReturnRate: rateField,
  inflationRate: rateField,
  expensesAtRetirement: z.number().default(0), // Can be negative (reduction)
  realEstateAppreciationRate: rateField,
  otherAssetGrowthRate: rateField,
  depreciationRate: rateField,
  cashEquivalentsPct: rateField,
  fixedIncomePct: rateField,
  equitiesPct: rateField,
  cashEquivalentsReturn: rateField,
  fixedIncomeReturn: rateField,
  equitiesReturn: rateField,
  healthcareInflationBuffer: rateField,
  withdrawalOrder: z.enum(WITHDRAWAL_ORDER_OPTIONS).optional(),
  estimatedRetirementTaxRate: rateField,
  desiredLegacyAmount: nonNegativeField,
});

/** Map from settings category to its Zod schema */
export const settingsSchemas = {
  profile: profileSchema,
  investableAssets: investableAssetsSchema,
  realEstateAssets: realEstateAssetsSchema,
  liabilities: liabilitiesSchema,
  income: incomeSchema,
  expenses: expensesSchema,
  assumptions: assumptionsSchema,
} as const;
